import React, { Key, ReactNode, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ExtendedTable, Header, SubheaderToolbarDivider } from '@app/components'
import { ContentLayout } from '@app/layouts'

import { usePaginationQueryParams, useToggle } from '@app/hooks'
import {
  getMonitoringCategories,
  getMonitoringCategoriesKinds,
  putMonitoringCategoriesKinds
} from '@app/api/routes/monitorings'

import { BUTTON_TYPE, SIZE } from '@app/constants'

import { Icon, Show } from '@app/components/util'

import { LntButton, LntDropdown } from '@app/components/base'

import { ICON_NAME } from '@app/constants/icon'

import { getExportFilesDropdownMenu } from '@app/utils'

import { FILE_TYPES } from '@app/api/constants'

import { exportMonitoringCategoriesKindsListFile } from '@app/utils/export'

import { FileType } from '@app/types'

import { CLASSIFIER_CONTROLS_KEY } from '@app/constants/queryKeys'

import { notification } from 'antd'

import { DefaultOptionType } from 'antd/es/select'

import { adaptClassiferControlsTableData, adaptClassiferMonitoringCategoriesData, getColumns } from './lib'

import { useClassifierControlEditableColumn } from './hooks'
import { EditedData } from './hooks/useClassifierControlEditableColumn/types'

const columns = getColumns()

const title = 'Управление классификатором'

export const ClassifierControlsPage: React.FC = () => {
  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Key[]>([])
  const [categoryNewValue, setCategoryNewValue] = useState<string>()
  const [stateEditedData, setStateEditedData] = useState<EditedData>([])
  const buttonLoadingExportAllToggle = useToggle()

  const constantCreator = (items: DefaultOptionType[]): Record<string, string> => {
    return items.reduce<Record<string, string>>((acc, item) => {
      acc[item.key] = item.label as string
      return acc
    }, {})
  }

  const { data: monitoringCategoriesData = [] } = useQuery({
    queryKey: [CLASSIFIER_CONTROLS_KEY.classifierCategoryListItems],
    queryFn: async () => await getMonitoringCategories(),
    select: monitoringCategoriesTypesData => adaptClassiferMonitoringCategoriesData(monitoringCategoriesTypesData)
  })

  const MONITORING_CATEGORY_KEYS = useMemo(() => {
    if (monitoringCategoriesData) {
      return constantCreator(monitoringCategoriesData)
    }
    return {}
  }, [monitoringCategoriesData])

  const REVERSED_MONITORING_CATEGORY_KEYS = Object.fromEntries(
    Object.entries(MONITORING_CATEGORY_KEYS).map(([key, value]) => [value, key])
  )

  const convertedCategoryId = (e: EditedData): EditedData => {
    return e?.map(el => {
      const mappedCategory = REVERSED_MONITORING_CATEGORY_KEYS[el.categoryId]
      return {
        ...el,
        categoryId: mappedCategory
      }
    })
  }

  const { data: classiferControlTableData, isLoading: isDataListsItemsLoading } = useQuery({
    queryKey: [CLASSIFIER_CONTROLS_KEY.classifierListItems, paginationParams],
    queryFn: async () => await getMonitoringCategoriesKinds(paginationParams),
    select: classifierTypesData => adaptClassiferControlsTableData(classifierTypesData, MONITORING_CATEGORY_KEYS)
  })

  const onSaveHandler = useMutation({
    mutationFn: putMonitoringCategoriesKinds,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [CLASSIFIER_CONTROLS_KEY.classifierListItems, paginationParams]
      })

      notification.success({
        message: 'Успешно изменено'
      })

      onClose()
    }
  })

  const handleExportAllMonitoringsCategories = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const fileName = 'Виды-категории'

    try {
      await exportMonitoringCategoriesKindsListFile(fileName, { file: fileType })
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const { columnsClassifierControl, editedData } = useClassifierControlEditableColumn({
    columns,
    selectedIds,
    categoryNewValue,
    monitoringCategoriesData,
    MONITORING_CATEGORY_KEYS
  })

  useEffect(() => {
    if (JSON.stringify(stateEditedData) !== JSON.stringify(editedData)) {
      setStateEditedData(editedData)
    }
  }, [editedData, monitoringCategoriesData])

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys)
  }

  const onClose = (): void => {
    setSelectedIds([])
    setStateEditedData([])
  }

  const onMultiChange = (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>): void => {
    const target = e.currentTarget
    const innerText = target.innerText
    setCategoryNewValue(innerText)
  }

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Виды-категории',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportAllMonitoringsCategories
  )

  const ButtonGroup = (): any => {
    return (
      <>
        { Object.entries(MONITORING_CATEGORY_KEYS).map(([key, label]) => (
          <React.Fragment key={ key }>
            <SubheaderToolbarDivider />

            <LntButton
              type={ BUTTON_TYPE.text }
              size={ SIZE.small }
              onClick={ e => onMultiChange(e) }
              key={ key }
            >
              { label }
            </LntButton>
          </React.Fragment>
        )) }
      </>
    )
  }

  const toolbar: ReactNode[] = [
    <Show
      key='save-btn'
      condition={ stateEditedData.length > 0 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ () => onSaveHandler.mutate(convertedCategoryId(editedData)) }
      >
        Сохранить
      </LntButton>

      <SubheaderToolbarDivider />
    </Show>,

    <Show
      key='export-btn'
      condition={ true }
    >
      <LntDropdown
        trigger={ ['click'] }
        items={ exportDropdownItems }
      >
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          loading={ buttonLoadingExportAllToggle.value }
        >
          Экспорт всех
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </LntDropdown>
    </Show>,

    <Show
      key='change-btn'
      condition={ selectedIds.length > 0 }
    >
      <ButtonGroup />
    </Show>
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <ContentLayout>
        <ExtendedTable
          loading={ isDataListsItemsLoading }
          columns={ columnsClassifierControl }
          dataSource={ classiferControlTableData?.dataSource }
          toolbar={ toolbar }
          pagination={ {
            ...pagination,
            total: classiferControlTableData?.count,
            onChange: handlePaginationChange
          } }
          rowSelection={ {
            onChange: handleSelectedRowChange,
            selectedRowKeys: selectedIds
          } }
        />
      </ContentLayout>
    </>
  )
}
